/**
 * The animated Lumio mark, beside the static LumioMark because every AI mark comes from this folder
 * (src/nova/CLAUDE.md). FTUX - Getting Started draws it in Lumio's hello, the menu map, Lumio's place and the
 * motion pictures; the static LumioMark, LumioGlyph and the asset are unchanged.
 *
 * The studio's pages import this folder eagerly, so the aurora's GSAP is loaded by a dynamic import on the first
 * mount and never lands in the pages' own chunks. It is the FTUX's motion/gsap, already loaded with the FTUX layer
 * wherever this mark shows.
 */
import React from 'react';

import { useReducedMotion } from '@/nova/hooks/useReducedMotion';
// Raw hex on purpose (the GlowField precedent): the mark's own colours, never recoloured by the light theme.
import { LUMIO_MOBILE as LUMIO_RAW } from '@/nova/theme/tokens.raw';

import { Bloom, Svg } from './LumioMarkAurora.styles';

/** The FTUX's GSAP, typed from its module (a type only: nothing loads before the first mount). */
type Gsap = (typeof import('@/nova/features/ftux/motion/gsap'))['gsap'];

/** The star's drawing box: its path is drawn in a 24 by 24 view box. */
export const LUMIO_STAR_BOX = 24;

/** The four-point Lumio star (LumioGlyph's path, public/assets/lumio/lumio-mark.svg). */
export const LUMIO_STAR_PATH =
  'M9.44905 1.82379C10.2548 -0.534285 13.5131 -0.605701 14.4684 1.60954L14.5493 1.82514L15.6367 5.00518C15.8859 5.73447 16.2886 6.40185 16.8177 6.96228C17.3467 7.52271 17.9898 7.96316 18.7036 8.25393L18.996 8.36308L22.176 9.44914C24.5341 10.2549 24.6055 13.5131 22.3916 14.4685L22.176 14.5493L18.996 15.6367C18.2664 15.8858 17.5988 16.2884 17.0381 16.8175C16.4774 17.3465 16.0367 17.9897 15.7458 18.7036L15.6367 18.9946L14.5506 22.176C13.7448 24.5341 10.4866 24.6055 9.53259 22.3916L9.44905 22.176L8.36298 18.996C8.11394 18.2664 7.7113 17.5988 7.18224 17.0381C6.65317 16.4775 6.01 16.0368 5.29611 15.7459L5.00506 15.6367L1.825 14.5507C-0.534441 13.7449 -0.605857 10.4867 1.6094 9.53268L1.825 9.44914L5.00506 8.36308C5.73436 8.11387 6.40173 7.71116 6.96217 7.18211C7.5226 6.65306 7.96306 6.00997 8.25383 5.29623L8.36298 5.00518L9.44905 1.82379Z';

/** The mark's own magenta and cyan (never the focus's Amber), the star at 75% as the product draws it. */
const MAGENTA = LUMIO_RAW.glowMagenta;
const CYAN = LUMIO_RAW.glowCyan;
const STAR_OPACITY = 0.75;

/**
 * The star's look at rest, for a picture that draws the star itself and hands over to this mark (Lumio's hello draws
 * its outline, then fills it): its two colours, its opacity, and its base sweep (a radial gradient from the top-left
 * corner, in the star's 24 units: magenta holds to `hold`, then eases into cyan).
 */
export const LUMIO_STAR_LOOK = {
  magenta: MAGENTA,
  cyan: CYAN,
  opacity: STAR_OPACITY,
  base: { cx: 6, cy: 5, r: 22, hold: 0.3 },
} as const;

/**
 * The colours inside the star, blended softly (no border, no highlight). At rest it is one smooth sweep: magenta over
 * the top and left, easing into cyan toward the bottom right. Three wide, faint lights (two magenta, one cyan) each
 * wander round a home of their own, so the blend moves while the star keeps its magenta-to-cyan read. Home and radius
 * in the star's 24 units.
 */
const LIGHTS = [
  { key: 'magenta-top', color: MAGENTA, home: [9, 6], r: 11, wander: 4 },
  { key: 'magenta-left', color: MAGENTA, home: [5, 14], r: 9, wander: 4 },
  { key: 'cyan', color: CYAN, home: [18, 17], r: 10, wander: 4 },
] as const;

/**
 * The aurora's pace: every light flows to a new place at once (smooth, about three seconds), then all of it rests
 * before the next flow. `size` is how much a light swells or shrinks. Each mark starts at its own moment, so marks on
 * one page never move in step.
 */
const AURORA = { flowS: 3.2, idleS: 2.4, size: [0.85, 1.2], startS: [0.4, 2.2] } as const;

/** The bloom's two blurs (in the star's 24 units): one close on its edge, one further out. */
const GLOW = { nearStd: 1.6, farStd: 4.5 } as const;

export type LumioMarkAuroraProps = {
  /** px (24 by default). */
  size?: number;
  className?: string;
  /** A soft bloom round the star in its own colours, moving with its aurora (softer on a light page). */
  glow?: boolean;
};

/** One mark's def ids (per instance, so any number of marks can share a page). */
type DefId = (name: string) => string;

/** The lights flow to new places together, then rest; every flow picks new places (repeatRefresh). */
const runAurora = (gsap: Gsap, svg: SVGSVGElement): void => {
  const swell = (): number => gsap.utils.random(AURORA.size[0], AURORA.size[1]);
  const flow = gsap.timeline({
    repeat: -1,
    repeatRefresh: true,
    delay: gsap.utils.random(AURORA.startS[0], AURORA.startS[1]),
  });
  LIGHTS.forEach(({ key, home, r, wander }) => {
    const light = svg.querySelector(`[data-light="${key}"]`);
    if (!light) return;
    const stray = (): number => gsap.utils.random(-wander, wander);
    const attr = { cx: () => home[0] + stray(), cy: () => home[1] + stray(), r: () => r * swell() };
    flow.to(light, { attr, duration: AURORA.flowS, ease: 'sine.inOut' }, 0);
  });
  flow.to({}, { duration: AURORA.idleS });
};

/** Starts the aurora once GSAP has loaded; the returned function stops it, or cancels a start still loading. */
const startAurora = (svg: SVGSVGElement): (() => void) => {
  let live = true;
  let stop: (() => void) | null = null;
  void import('@/nova/features/ftux/motion/gsap')
    .then(({ gsap }) => {
      if (!live) return;
      const context = gsap.context(() => runAurora(gsap, svg), svg);
      stop = () => context.revert();
    })
    // Without its motion chunk the mark stays still, exactly as it rests under reduced motion.
    .catch(() => undefined);
  return () => {
    live = false;
    stop?.();
  };
};

const AuroraDefs = ({ id, glow }: { id: DefId; glow: boolean }): React.ReactElement => (
  <defs>
    <clipPath id={id('clip')}>
      <path d={LUMIO_STAR_PATH} />
    </clipPath>
    {/* Radial, from the top-left corner: magenta holds a third of the way, then eases into cyan along a curve. */}
    <radialGradient
      id={id('base')}
      cx={LUMIO_STAR_LOOK.base.cx}
      cy={LUMIO_STAR_LOOK.base.cy}
      r={LUMIO_STAR_LOOK.base.r}
      gradientUnits="userSpaceOnUse"
    >
      <stop stopColor={MAGENTA} />
      <stop offset={LUMIO_STAR_LOOK.base.hold} stopColor={MAGENTA} />
      <stop offset="1" stopColor={CYAN} />
    </radialGradient>
    {LIGHTS.map((light) => (
      <radialGradient key={light.key} id={id(light.key)}>
        <stop stopColor={light.color} stopOpacity="0.55" />
        <stop offset="0.5" stopColor={light.color} stopOpacity="0.28" />
        <stop offset="1" stopColor={light.color} stopOpacity="0" />
      </radialGradient>
    ))}
    {glow ? (
      // The star blurred twice, close and further out: its own colours spill round it (the star is drawn over it).
      <filter id={id('bloom')} x="-1" y="-1" width="3" height="3" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation={GLOW.nearStd} result="near" />
        <feGaussianBlur in="SourceGraphic" stdDeviation={GLOW.farStd} result="far" />
        <feMerge>
          <feMergeNode in="far" />
          <feMergeNode in="near" />
        </feMerge>
      </filter>
    ) : null}
  </defs>
);

/**
 * The Lumio brand mark, alive: the brand's star in its own magenta and cyan, no border, tile, plate or sheen, the two
 * blended inside it like an aurora that flows, rests a moment, then flows again (still under reduced motion). Only the
 * colours move; the star's edge never does, so it stays crisp. With `glow`, a bloom in the same colours moves with it.
 * Decorative.
 *
 * A holder that moves or scales it does so in 2D, never on a layer of its own (no will-change, no translate3d): Chrome
 * keeps a will-change layer as one bitmap at its native size, so the star read as a low-resolution picture wherever
 * the hello and the map shrank it to Lumio's place. Drawn with the page, it is a vector at every size.
 */
export const LumioMarkAurora = ({ size = 24, className, glow = false }: LumioMarkAuroraProps): React.ReactElement => {
  const uid = React.useId().replace(/:/g, '');
  const id: DefId = (name) => `lumio-aurora-${name}-${uid}`;
  const url = (name: string): string => `url(#${id(name)})`;
  const svgRef = React.useRef<SVGSVGElement>(null);
  const reducedMotion = useReducedMotion();

  React.useEffect(() => {
    const svg = svgRef.current;
    if (reducedMotion || !svg) return undefined;
    return startAurora(svg);
  }, [reducedMotion]);

  return (
    <Svg ref={svgRef} $size={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <AuroraDefs id={id} glow={glow} />
      <g opacity={STAR_OPACITY}>
        {/* The bloom mirrors the star and its moving lights (a live copy), under it. */}
        {glow ? (
          <Bloom filter={url('bloom')}>
            <use href={`#${id('art')}`} />
          </Bloom>
        ) : null}
        <g id={id('art')}>
          <path d={LUMIO_STAR_PATH} fill={url('base')} />
          <g clipPath={url('clip')}>
            {LIGHTS.map((light) => (
              <circle
                key={light.key}
                data-light={light.key}
                cx={light.home[0]}
                cy={light.home[1]}
                r={light.r}
                fill={url(light.key)}
              />
            ))}
          </g>
        </g>
      </g>
    </Svg>
  );
};
